import React, { useMemo, useState } from 'react';
import { Card } from '../ui';
import { ArrowDownTrayIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { useChildren } from '../../hooks/api/useChildren';
import { dataExportApi } from '../../services/api/dataExport';
import { useToast } from '../ui/ToastProvider';
import { extractApiErrorMessage } from '../../utils/apiError';
import type { Child } from '../../types/api';

const formatChildName = (child: Child) => `${child.name} ${child.surname}`.trim();

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '') || 'child';

const triggerJsonDownload = (filename: string, payload: unknown) => {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const DataExportCard: React.FC = () => {
  const { data: children, isLoading } = useChildren();
  const toast = useToast();

  const sortedChildren = useMemo(
    () => [...(children?.items ?? [])].sort((a, b) => formatChildName(a).localeCompare(formatChildName(b))),
    [children],
  );

  const [selectedId, setSelectedId] = useState<string>('');
  const [exporting, setExporting] = useState(false);

  const selectedChild = sortedChildren.find((c) => c.id === selectedId) ?? null;

  const handleExport = async () => {
    if (!selectedChild) {
      toast.error('Pick a child', 'Choose a child from the list to export.');
      return;
    }

    setExporting(true);
    try {
      const payload = await dataExportApi.exportChild(selectedChild.id);
      const today = new Date().toISOString().slice(0, 10);
      triggerJsonDownload(`sprout-export-${slugify(formatChildName(selectedChild))}-${today}.json`, payload);
      toast.success(
        'Export downloaded',
        `${formatChildName(selectedChild)}'s record was saved to your device.`,
      );
    } catch (err) {
      toast.error(
        'Export failed',
        extractApiErrorMessage(err, 'Please try again or contact support.'),
      );
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-start gap-3 mb-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center">
          <DocumentArrowDownIcon className="w-5 h-5 text-primary-600 dark:text-primary-300" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Data Export</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Download a child's full record as JSON — observations, assessments, consents, and audit
            metadata. Available on every plan to support POPIA Section 23 access requests.
          </p>
        </div>
      </div>

      <div className="space-y-3">
        <label htmlFor="data-export-child" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Child
        </label>
        <select
          id="data-export-child"
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
          disabled={isLoading || sortedChildren.length === 0}
          className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 disabled:opacity-60"
        >
          <option value="">{isLoading ? 'Loading children…' : 'Select a child to export'}</option>
          {sortedChildren.map((child) => (
            <option key={child.id} value={child.id}>
              {formatChildName(child)}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={handleExport}
          disabled={!selectedId || exporting}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <ArrowDownTrayIcon className="w-4 h-4" />
          {exporting ? 'Preparing export…' : 'Download JSON'}
        </button>

        <p className="text-xs text-gray-500 dark:text-gray-400 pt-2">
          Bulk export (whole organisation or a classroom) and additional formats (CSV, PDF) are on
          the roadmap. For larger requests, contact support.
        </p>
      </div>
    </Card>
  );
};

export default DataExportCard;
